import React, { useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, X, CheckCheck, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Z_CLASS } from '../../constants/zIndex';

/**
 * NotificationCenter - In-app notification bell + history drawer
 * Shows in TopStatusBar area, slides down as a panel
 */
export const NotificationCenter = memo(function NotificationCenter({
  notifHistory = [],
  unreadCount = 0,
  onMarkAllRead,
  onMarkRead,
  onClearHistory,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showYesterday, setShowYesterday] = useState(false);

  const toggle = useCallback(() => {
    setIsOpen(prev => {
      if (!prev && unreadCount > 0) {
        // Auto-mark as read when opening
        onMarkAllRead?.();
      }
      return !prev;
    });
  }, [unreadCount, onMarkAllRead]);

  // Split notifications into today vs older
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  const todayNotifs = notifHistory.filter(n => n.timestamp >= todayStart.getTime());
  const olderNotifs = notifHistory.filter(n => n.timestamp < todayStart.getTime());

  return (
    <>
      {/* Bell button */}
      <button
        onClick={toggle}
        className="relative p-1.5 rounded-lg hover:bg-zinc-800/60 transition-colors"
        title="Centre de notifications"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} non lues)` : ''}`}
      >
        {unreadCount > 0 ? (
          <Bell className="w-4 h-4 text-zinc-300" />
        ) : (
          <BellOff className="w-4 h-4 text-zinc-600" />
        )}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 bg-red-500 rounded-full
                flex items-center justify-center text-[9px] font-bold text-white tabular-nums"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={`fixed inset-0 ${Z_CLASS.NOTIFICATION_CENTER} bg-black/40`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Notification drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={`fixed top-[52px] right-0 left-0 max-w-md mx-auto ${Z_CLASS.NOTIFICATION_CENTER}
              bg-[var(--bg-surface,#0c0c0f)] border-b border-x border-[var(--border-subtle,rgba(255,255,255,0.08))]
              rounded-b-2xl shadow-2xl overflow-hidden`}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/60">
              <span className="text-sm font-semibold text-zinc-200">Notifications</span>
              <div className="flex items-center gap-2">
                {notifHistory.length > 0 && (
                  <button
                    onClick={onClearHistory}
                    className="text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors flex items-center gap-1"
                    title="Effacer l'historique"
                  >
                    <Trash2 className="w-3 h-3" />
                    Effacer
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded hover:bg-zinc-800 transition-colors"
                >
                  <X className="w-4 h-4 text-zinc-500" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
              {notifHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <Bell className="w-8 h-8 text-zinc-700" />
                  <p className="text-sm text-zinc-600">Aucune notification</p>
                </div>
              ) : (
                <>
                  {/* Today's notifications */}
                  {todayNotifs.length > 0 && (
                    <div>
                      <div className="px-4 pt-3 pb-1">
                        <span className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wider">Aujourd'hui</span>
                      </div>
                      {todayNotifs.map(notif => (
                        <NotifRow key={notif.id} notif={notif} onMarkRead={onMarkRead} />
                      ))}
                    </div>
                  )}

                  {/* Older notifications (collapsible) */}
                  {olderNotifs.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowYesterday(prev => !prev)}
                        className="w-full flex items-center gap-2 px-4 pt-3 pb-1 hover:bg-zinc-800/30 transition-colors"
                      >
                        {showYesterday ? (
                          <ChevronDown className="w-3 h-3 text-zinc-600" />
                        ) : (
                          <ChevronRight className="w-3 h-3 text-zinc-600" />
                        )}
                        <span className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wider">
                          Précédentes ({olderNotifs.length})
                        </span>
                      </button>
                      <AnimatePresence>
                        {showYesterday && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="overflow-hidden"
                          >
                            {olderNotifs.map(notif => (
                              <NotifRow key={notif.id} notif={notif} onMarkRead={onMarkRead} />
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            {notifHistory.length > 0 && (
              <div className="px-4 py-2.5 border-t border-zinc-800/60">
                <p className="text-[10px] text-zinc-700 text-center">
                  {notifHistory.length} notification{notifHistory.length > 1 ? 's' : ''} · 50 max conservées
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

/**
 * Individual notification row
 */
const NotifRow = memo(function NotifRow({ notif, onMarkRead }) {
  const relativeTime = formatRelativeTime(notif.timestamp);
  const categoryEmoji = {
    vital: '🔴',
    chore: '📋',
    pleasure: '✨',
    default: '🔔',
  }[notif.category] || '🔔';

  return (
    <motion.div
      layout
      className={`px-4 py-2.5 flex items-start gap-3 hover:bg-zinc-800/30 transition-colors
        ${!notif.read ? 'bg-zinc-800/20' : ''}`}
      onClick={() => onMarkRead?.(notif.id)}
    >
      <span className="text-base mt-0.5 flex-shrink-0">{categoryEmoji}</span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug ${notif.read ? 'text-zinc-500' : 'text-zinc-200'}`}>
          {notif.body}
        </p>
        <p className="text-[10px] text-zinc-700 mt-0.5">{relativeTime}</p>
      </div>
      {!notif.read && (
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
      )}
    </motion.div>
  );
});

function formatRelativeTime(timestamp) {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'à l\'instant';
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}
